import React from 'react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { APP_URL } from '../lib/appUrl';
import {
    ArrowRightIcon,
    TicketIcon,
    QrCodeIcon,
    CurrencyDollarIcon,
    UsersThreeIcon,
    GiftIcon,
    ChartBarIcon,
    ShieldCheckIcon,
    ChatCircleTextIcon,
    MicrophoneStageIcon,
    EyeSlashIcon,
    StackIcon,
    ConfettiIcon,
    CalendarBlankIcon,
    LifebuoyIcon,
    SparkleIcon,
} from '@phosphor-icons/react';

export default function Landing() {
    return (
        <div className="min-h-screen grain scanlines">
            <Navbar />

            {/* HERO — KEEP EXACTLY */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 -z-0">
                    <img
                        src="https://images.unsplash.com/photo-1591833417792-0d3d96df6159?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
                        alt="Crowd on a dark dance floor under stage lights at a nightclub event"
                        loading="eager"
                        decoding="async"
                        className="w-full h-full object-cover opacity-50 saturate-50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050506] via-[#050506]/70 to-[#050506]/20" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-32">
                    <div className="flex items-center gap-4 mb-12 fade-up" data-testid="hero-overline">
                        <span className="inline-block w-2 h-2 rounded-full bg-neon animate-pulse" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon">
                            The ticketing app for nights worth remembering
                        </span>
                    </div>
                    <h1
                        className="font-display uppercase tracking-tight leading-[0.82] text-white mb-8 fade-up"
                        data-testid="hero-title"
                    >
                        <span className="block text-7xl sm:text-9xl lg:text-[12rem] xl:text-[14rem]">Tonight</span>
                        <span className="block text-7xl sm:text-9xl lg:text-[12rem] xl:text-[14rem] text-neon neon-glow">
                            isn't
                        </span>
                        <span className="block text-7xl sm:text-9xl lg:text-[12rem] xl:text-[14rem]">over.</span>
                    </h1>
                    <p className="text-base sm:text-xl text-white/70 max-w-2xl leading-relaxed mb-10 fade-up">
                        Portal is the ticketing platform built for inclusive nightlife — warehouse parties, drag
                        shows, after-hours, brunches, and every room in between. Multi-tier tickets, direct
                        payouts. Built for the people who throw the night, and the people who walk in.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 fade-up">
                        <a href={APP_URL}>
                            <Button
                                className="bg-neon text-black hover:bg-[#E8FF6B] rounded-none px-10 py-7 text-sm uppercase tracking-[0.2em] font-bold"
                                data-testid="hero-getin-btn"
                            >
                                Get in <ArrowRightIcon size={16} weight="bold" className="ml-3" />
                            </Button>
                        </a>
                    </div>
                </div>

                {/* Marquee */}
                <div className="relative border-t border-b border-neon/30 bg-neon text-black overflow-hidden">
                    <div className="marquee-track flex whitespace-nowrap py-4 font-display uppercase text-2xl sm:text-3xl tracking-tight">
                        {Array.from({ length: 2 }).map((_, k) => (
                            <div key={k} className="flex items-center shrink-0">
                                {[
                                    'Multi-tier tickets',
                                    'Stripe checkout',
                                    'Off-grid events',
                                    'Auto-rolling tiers',
                                    'Drag · Warehouse · After hours',
                                    'Direct organizer payouts',
                                    'Lineup tools',
                                ].map((t, i) => (
                                    <span key={`${k}-${i}`} className="flex items-center px-8">
                                        <span>{t}</span>
                                        <span className="mx-8 text-black/50">✦</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT IT DOES */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 border-b border-white/10">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-6">
                            // 01 · What it is
                        </p>
                        <h2 className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-[0.9]">
                            Ticketing built<br />
                            for the <span className="text-neon">underground.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-8 space-y-6 text-white/70 text-base lg:text-lg leading-relaxed">
                        <p>
                            Portal is two products in one. For attendees, it's a clean wallet of QR-coded passes
                            sorted by what's coming up next — discover events by category (nightlife, drag show,
                            warehouse, after hours, brunch, music, food, arts), buy in two taps, walk in.
                        </p>
                        <p>
                            For organizers, it's a thirteen-tab producer console covering every stage of running a
                            night — from publishing and pricing to comps, promo codes, lineups, marketing blasts,
                            door scanning, and direct payouts to your bank.
                        </p>
                        <p className="text-white text-lg lg:text-xl">
                            One platform. Free RSVPs to sold-out warehouse drops. Transparent fees, no contracts.
                        </p>
                    </div>
                </div>
            </section>

            {/* TICKET ENGINE */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 border-b border-white/10">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">
                            // 02 · Ticket engine
                        </p>
                        <h2 className="font-display uppercase text-5xl sm:text-7xl tracking-tight leading-none">
                            Every tier.<br />
                            Every <span className="text-neon">curve ball.</span>
                        </h2>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                    {[
                        {
                            icon: StackIcon,
                            title: 'Unlimited tiers',
                            desc: 'Paid, free, or pay-what-you-can. Add early bird, GA, VIP, dinner-included — each with its own perks list.',
                        },
                        {
                            icon: CalendarBlankIcon,
                            title: 'Sales windows',
                            desc: 'Schedule each tier with its own start/end. Or chain them — when GA sells out, late release auto-opens.',
                        },
                        {
                            icon: GiftIcon,
                            title: 'Comp & guest lists',
                            desc: 'Issue free passes to press, friends, and the lineup. They get the same QR code, you keep the count.',
                        },
                        {
                            icon: CurrencyDollarIcon,
                            title: 'Promo codes',
                            desc: '% or $ off, capped uses, expiry windows. Track which code drove which sale.',
                        },
                        {
                            icon: UsersThreeIcon,
                            title: 'Waitlists',
                            desc: 'Sold-out doesn\'t mean lights out. Waitlists capture demand and convert when comps free up.',
                        },
                        {
                            icon: TicketIcon,
                            title: 'Transfers & refunds',
                            desc: 'Per-tier toggles. Allow ticket transfers, set a no-refund policy, or absorb fees into the price.',
                        },
                    ].map((p, i) => (
                        <div
                            key={p.title}
                            className="bg-[#0A0A0B] p-10 lg:p-12 fade-up group"
                            style={{ animationDelay: `${i * 60}ms` }}
                        >
                            <div className="flex items-start justify-between mb-8">
                                <p.icon size={36} weight="duotone" className="text-neon" />
                                <span className="font-mono text-xs text-white/30">0{i + 1}</span>
                            </div>
                            <h3 className="font-display uppercase text-3xl mb-4 group-hover:text-neon transition-colors">
                                {p.title}
                            </h3>
                            <p className="text-white/60 text-sm leading-relaxed">{p.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* PRODUCER CONSOLE */}
            <section className="border-b border-white/10 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="mb-12">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">
                            // 03 · The producer console
                        </p>
                        <h2 className="font-display uppercase text-5xl sm:text-7xl tracking-tight leading-[0.9]">
                            Thirteen tabs.<br />
                            <span className="text-neon">Zero clipboards.</span>
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-2xl mb-12 text-base lg:text-lg leading-relaxed">
                        Every job that used to live in a group chat, a spreadsheet, and three Notion pages — now in
                        one screen.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
                        {[
                            { i: ChartBarIcon, t: 'Overview', d: 'Net payout, gross, fees, refunds. Sales by tier.' },
                            { i: TicketIcon, t: 'Manage tickets', d: 'Multi-tier pricing, perks, sales windows.' },
                            { i: GiftIcon, t: 'Comp', d: 'Issue free passes to VIPs, press, lineup.' },
                            { i: CurrencyDollarIcon, t: 'Promos', d: '% or $ off codes with caps and expiry.' },
                            { i: MicrophoneStageIcon, t: 'Lineup', d: 'DJs, hosts, drag queens — set times included.' },
                            { i: ChatCircleTextIcon, t: 'Marketing', d: 'Email blasts to your attendees.' },
                            { i: ShieldCheckIcon, t: 'Permissions', d: 'Co-hosts, scanner roles, door staff.' },
                            { i: ConfettiIcon, t: 'Publishing', d: 'Submit for approval, schedule the drop.' },
                            { i: UsersThreeIcon, t: 'Guests', d: 'Search, filter, scan, mark arrived.' },
                            { i: CurrencyDollarIcon, t: 'Finance', d: 'Earnings, fees, payouts, Stripe transfers.' },
                            { i: TicketIcon, t: 'Waitlist', d: 'Captured demand once you sell out.' },
                            { i: ChatCircleTextIcon, t: 'Feedback', d: 'Post-event reviews from your room.' },
                            { i: LifebuoyIcon, t: 'Support', d: 'Direct line to the Portal team, per event.' },
                        ].map((item, i) => (
                            <div
                                key={item.t}
                                className="bg-[#050506] p-6 lg:p-8 fade-up hover:bg-[#121214] transition-colors"
                                style={{ animationDelay: `${i * 30}ms` }}
                            >
                                <item.i size={22} weight="duotone" className="text-neon mb-4" />
                                <h3 className="font-display uppercase text-2xl mb-2">{item.t}</h3>
                                <p className="text-white/50 text-xs leading-relaxed">{item.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OFF-GRID + DOOR */}
            <section className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">
                            // 04 · Off-grid mode
                        </p>
                        <h2 className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-[0.9] mb-6">
                            Public room.<br />
                            <span className="text-neon">Or invite-only.</span>
                        </h2>
                        <p className="text-white/70 leading-relaxed mb-4">
                            Flip a single switch and your event drops out of public listings. The link still works,
                            tickets still sell, the door still scans — but it only shows up for people who already
                            have the URL.
                        </p>
                        <p className="text-white/60 text-sm leading-relaxed">
                            Built for residencies, members-only nights, after-parties that aren't supposed to be
                            findable.
                        </p>
                    </div>
                    <div className="border border-white/10 bg-[#0A0A0B] p-8">
                        <div className="flex items-start gap-4 mb-6">
                            <EyeSlashIcon size={32} weight="duotone" className="text-neon" />
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                    // Off-grid · ON
                                </p>
                                <p className="font-display text-2xl uppercase">Hidden from /discover</p>
                            </div>
                        </div>
                        <div className="space-y-2 text-xs text-white/60">
                            {[
                                ['Public listings', 'Hidden'],
                                ['Direct link', 'Active · sells tickets'],
                                ['Door scanner', 'Active'],
                                ['Search engines', 'noindex'],
                                ['Calendar of events', 'Excluded'],
                                ['Refund / transfer', 'Per-tier policy still applies'],
                            ].map(([k, v]) => (
                                <div key={k} className="flex justify-between border-t border-white/5 py-2">
                                    <span>{k}</span>
                                    <span className="text-neon font-mono">{v}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* DOOR + PAYOUTS */}
            <section className="border-b border-white/10 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div className="border border-white/10 bg-[#050506] p-8 order-2 md:order-1">
                        <div className="flex items-start gap-4 mb-6">
                            <QrCodeIcon size={32} weight="duotone" className="text-neon" />
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                    // Tonight
                                </p>
                                <p className="font-display text-2xl uppercase">247 scans · 12 voids</p>
                            </div>
                        </div>
                        <div className="space-y-2 text-xs text-white/60">
                            {[
                                ['GA · 21:00 tier', '180 / 200 sold'],
                                ['Late release', 'Auto-opened at 23:00'],
                                ['Comp · press', '14 / 20 redeemed'],
                                ['Promo · LOCALSONLY', '34 uses · 25% off'],
                                ['Refunds', '3 processed'],
                                ['Net payout', '$8,420 · transferred'],
                            ].map(([k, v]) => (
                                <div key={k} className="flex justify-between border-t border-white/5 py-2">
                                    <span>{k}</span>
                                    <span className="text-neon font-mono">{v}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="order-1 md:order-2">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">
                            // 05 · Door & payouts
                        </p>
                        <h2 className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-[0.9] mb-6">
                            Scan in.<br />
                            <span className="text-neon">Cash out.</span>
                        </h2>
                        <p className="text-white/70 leading-relaxed mb-4">
                            Every ticket is a unique QR code with a status (active / used / refunded). Door staff
                            scan from any phone — no app install, no kiosk. Voids and dupes show up immediately.
                        </p>
                        <p className="text-white/70 leading-relaxed">
                            When the night ends, Portal calculates your net after card fees and pushes the payout
                            to your connected Stripe account. Transparent line items in the Finance tab. No
                            reconciliation spreadsheet.
                        </p>
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="border-b border-white/10 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
                    <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">
                        // 06 · Every kind of night
                    </p>
                    <h2 className="font-display uppercase text-5xl sm:text-7xl tracking-tight leading-[0.9] mb-12">
                        From <span className="text-neon">brunch</span> to<br />
                        warehouse <span className="text-neon">after-hours.</span>
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        {[
                            'Nightlife',
                            'Drag show',
                            'Warehouse',
                            'After hours',
                            'Brunch',
                            'Music',
                            'Food',
                            'Arts',
                            'Sports',
                        ].map((c) => (
                            <span
                                key={c}
                                className="inline-flex items-center gap-2 border border-white/15 bg-[#050506] text-white/80 px-5 py-3 text-xs uppercase tracking-[0.25em] font-mono hover:border-neon hover:text-neon transition-colors"
                            >
                                <SparkleIcon size={12} weight="duotone" className="text-neon" />
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="border-b border-white/10">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24">
                    <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">// 07 · Small print</p>
                    <h2 className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-none mb-12">
                        Questions.
                    </h2>
                    <div className="divide-y divide-white/10">
                        {[
                            {
                                q: 'How fast does my event go live?',
                                a: 'New organizers get a one-time approval review (we filter out scams and hate-group events). Once approved, future drops publish instantly.',
                            },
                            {
                                q: 'How do payouts actually work?',
                                a: 'Connect your Stripe account once. Portal collects payments at checkout, deducts card processing fees, and transfers your net to your bank — itemized in the Finance tab.',
                            },
                            {
                                q: 'Can I sell free RSVPs alongside paid tiers?',
                                a: 'Yes. Each event can have unlimited tiers — paid, free, or pay-what-you-can — running side by side, each with its own sales window and perks.',
                            },
                            {
                                q: "What's an off-grid event?",
                                a: "An event that's hidden from public discover and search engines but still works via direct link. Sales, scanning, and tracking all function normally.",
                            },
                            {
                                q: 'Do tier sales windows have to be manual?',
                                a: 'No. You can chain them — set a Late Release tier to auto-open the moment GA sells out, or schedule them by date/time.',
                            },
                            {
                                q: 'How do you handle transfers and refunds?',
                                a: 'Per-tier toggles. Allow transfers, no-refund policy, and fee absorption are independent settings on every ticket type.',
                            },
                            {
                                q: 'What kind of events get rejected?',
                                a: 'Anything we believe will harm the people walking in — hate-group line-ups, transphobic door policies, fascist organizers. No platforming, no debate.',
                            },
                        ].map((f) => (
                            <details key={f.q} className="group py-6">
                                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                                    <h3 className="font-display uppercase text-2xl tracking-tight leading-tight group-hover:text-neon transition-colors">
                                        {f.q}
                                    </h3>
                                    <span className="font-mono text-neon text-xl pt-1 group-open:rotate-45 transition-transform">
                                        +
                                    </span>
                                </summary>
                                <p className="text-white/70 mt-4 max-w-2xl leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section>
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 grid md:grid-cols-2 gap-12 items-center">
                    <h2 className="font-display uppercase text-5xl lg:text-8xl tracking-tight leading-[0.85]">
                        See you<br />
                        on the <span className="text-neon">floor.</span>
                    </h2>
                    <div className="flex md:justify-end">
                        <a href={APP_URL}>
                            <Button
                                className="bg-neon text-black hover:bg-[#E8FF6B] rounded-none px-12 py-7 text-sm uppercase tracking-[0.2em] font-bold"
                                data-testid="cta-getin-btn"
                            >
                                <TicketIcon size={14} weight="bold" className="mr-2" />
                                Get in
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            <footer className="border-t border-white/10 py-10 text-center text-[10px] text-white/40 font-mono uppercase tracking-[0.3em]">
                © Portal 2026 // Built for nights worth losing
            </footer>
        </div>
    );
}
